/*
 * @providesModule FTUEConversionsController
 */
import {FTUETrainerTextProps} from '@skillz/ui';
import AssetHelper from 'AssetHelper';
import ClientPropertyConstants from 'ClientPropertyConstants';
import FTUEConstants from 'FTUEConstants';
import FTUEController from 'FTUEController';
import GlobalEventEmitter from 'GlobalEventEmitter';
import {Interstitials} from 'InterstitialConstants';
import {InterstitialProps} from 'InterstitialModalUtils';
import InterstitialsManager from 'InterstitialsManager';
import I18n from 'INTL';
import Preferences from 'Preferences';
import {Dimensions, Image, Platform} from 'react-native';
import {
  BUBBLE_ANIMATION,
  BUBBLE_TEXT_CONTAINER_STYLE,
  BUBBLE_TEXT_PROPS,
  BUBBLE_TEXT_STYLE,
} from './FTUEConversions.config';
import {
  EXPECTED_INTERSTITIALS_PER_STEP,
  FTUEConversionsInterstitials,
  FTUEConversionsStep,
  ORDERED_CONVERSIONS_STEPS,
  TRAINER_ASSET_KEYS,
  TutorialTrainerStep,
  TutorialTrainerTextOptions,
} from './FTUEConversions.types';

/**
 * Manages logic for the FTUE conversions flow, in which the user will earn
 * bonus cash to play a cash match as part of their FTUE
 */
class FTUEConversionsController {
  /**
   * The index of the user's current step in the flow; this is initialized to
   * the last step index to assume completion (don't do anything more in the
   * flow) before we know otherwise
   */
  private _currentStepIndex = ORDERED_CONVERSIONS_STEPS.length - 1;

  /**
   * The interstitials to show during the FTUE conversions flow when the user
   * completes certain steps
   */
  private _interstitials: FTUEConversionsInterstitials = {};

  /**
   * Optional flag to manually disable the FTUE conversions flow; this is used
   * in cases where the data we need (interstitials, etc.) is unavailable
   */
  private _disabled = false;

  /**
   * The most recent step in the tutorial trainer flow that the user finished in
   * the current session; we use this to show tutorial trainer text after the
   * user has completed a step
   */
  private _lastCompletedStep: FTUEConversionsStep | undefined;

  constructor() {
    this.initializeFromPreferences();
    this.addListeners();
  }

  get trainerBotEnabled(): boolean {
    return (
      !ClientPropertyConstants.getFTUESkipMatchTutorial() &&
      ClientPropertyConstants.getIsAsyncFtueBotEnabled() &&
      ClientPropertyConstants.getFTUEType() === 'Async'
    );
  }

  get enabled() {
    // If the training bot is enabled for this game, we'll consider this the
    // "conversions" flow

    // The conversions flow is disabled if the user has skipped the FTUE flow
    return !this._disabled && this.trainerBotEnabled;
  }

  get completed() {
    return this.enabled && this.currentStep === 'completed';
  }

  get currentStep(): FTUEConversionsStep {
    // Assume the user has completed the conversions flow if they've seen the
    // match select tutorial
    return FTUEController.hasSeenMatchSelectTutorial()
      ? 'completed'
      : ORDERED_CONVERSIONS_STEPS[this._currentStepIndex];
  }

  get lastCompletedStep(): FTUEConversionsStep | undefined {
    if (!this.enabled) return;

    // If the user completed a step within this session, return it
    if (this._lastCompletedStep) return this._lastCompletedStep;

    // If the user hasn't completed the flow, return the step before their
    // current step
    // If the user already completed the flow, we'll assume they've seen all the
    // tutorial text and return "completed" instead
    return this.completed ? 'completed' : ORDERED_CONVERSIONS_STEPS[this._currentStepIndex - 1];
  }

  get isTrainingMatch(): boolean {
    return (
      this.enabled &&
      !FTUEController.hasSeenMatchSelectTutorial() &&
      ['playTrainingMatch1', 'playTrainingMatch2'].includes(this.currentStep)
    );
  }

  /**
   * Specifies the index of the template that should be used for a training match
   *
   * Usually, this is just the cheapest entry fee template, but we use the
   * second cheapest template for the second training match
   */
  get trainingMatchTemplateIndex(): number {
    return this.currentStep === 'playTrainingMatch2' ? 1 : 0;
  }

  getTutorialTrainerProps(
    step: TutorialTrainerStep | undefined,
    {overlayVisible = false, nameVisible = false, textValues}: TutorialTrainerTextOptions = {}
  ): FTUETrainerTextProps | undefined {
    if (!this.enabled || !step) return;

    const assetConfig = this.getTrainerAssetConfig(step);
    if (!assetConfig) return;

    const text = this.getTrainerText(step, textValues);
    if (!text) return;

    return {
      ...assetConfig,
      text,
      overlayVisible,
      trainerName: nameVisible ? I18n.message('TutorialTrainer') : undefined,
      ...this.getTrainerPosition(assetConfig.arrowSide),
      textStyle: BUBBLE_TEXT_STYLE,
      textContainerStyle: BUBBLE_TEXT_CONTAINER_STYLE,
      textProps: BUBBLE_TEXT_PROPS,
      bubbleAnimation: {
        ...BUBBLE_ANIMATION,
        delay: this.getBubbleAnimationDelay(step),
      },
    };
  }

  getExpectedTutorialTrainerStep(inMatch = false): TutorialTrainerStep | undefined {
    switch (this.currentStep) {
      case 'playTrainingMatch1':
        return inMatch ? 'trainingMatch1Versus' : 'trainingMatch1Results';
      case 'playTrainingMatch2':
        return inMatch ? 'trainingMatch2Versus' : 'trainingMatch2Select';
      case 'completed':
        return this.lastCompletedStep === 'playTrainingMatch2'
          ? 'trainingMatch2Results'
          : undefined;
      default:
        return;
    }
  }

  advance(completedStep: FTUEConversionsStep, onComplete?: (advanced: boolean) => void): void {
    if (!this.enabled) return onComplete?.(false);

    // If the user isn't on the step we're trying to complete, don't do anything
    if (this.currentStep !== completedStep) return onComplete?.(false);

    // Update the step index
    this._lastCompletedStep = completedStep;
    this._currentStepIndex++;
    Preferences.set({
      [FTUEConstants.conversionsStepIndex]: this._currentStepIndex,
    });
    this.onCompleteStep(completedStep);

    const stepInterstitials = this._interstitials[completedStep];
    if (!stepInterstitials?.length) return onComplete?.(true);

    // Show the interstitials for the completed step
    InterstitialsManager.showInterstitials(stepInterstitials, () => onComplete?.(true));
  }

  skip() {
    // If the flow already isn't enabled, we don't need to do anything
    if (!this.enabled) return;

    this._disabled = true;
    Preferences.set({
      [FTUEConstants.conversionsDisabled]: true,
      [FTUEConstants.conversionsStepIndex]: ORDERED_CONVERSIONS_STEPS.length - 1,
    });
  }

  async load() {
    if (!this.enabled) return;

    // We want to preload the trainer images so that they're available when the
    // user enters the versus screen for the first time
    await Promise.all([this.loadInterstitials(), this.loadTrainerImages()]);
  }

  async loadInterstitials() {
    if (!this.enabled) return;

    // Don't reload if we already have interstitials
    if (Object.keys(this._interstitials).length) return;

    const interstitials = await InterstitialsManager.sendTrigger(Interstitials.FTUE_START, {
      skipPopup: true,
    });
    if (interstitials?.length) {
      const conversionsInterstitials = this.splitInterstitialsByStep(interstitials);
      this._interstitials = conversionsInterstitials;
      Preferences.set({
        [FTUEConstants.conversionsInterstitials]: JSON.stringify(conversionsInterstitials),
      });
    }
  }

  private splitInterstitialsByStep(interstitials: InterstitialProps[]) {
    if (!interstitials.length) {
      return {};
    }

    const conversionsInterstitials: FTUEConversionsInterstitials = {};
    let splitStart = 0;
    for (const [step, interstitialCount] of Object.entries(EXPECTED_INTERSTITIALS_PER_STEP)) {
      const splitEnd = splitStart + interstitialCount;
      conversionsInterstitials[step as FTUEConversionsStep] = interstitials.slice(
        splitStart,
        splitEnd
      );
      splitStart = splitEnd;
      if (splitStart >= interstitials.length) {
        break;
      }
    }
    return conversionsInterstitials;
  }

  private initializeFromPreferences() {
    this._disabled = Preferences.get(FTUEConstants.conversionsDisabled) ?? false;
    this._currentStepIndex = Preferences.get(FTUEConstants.conversionsStepIndex) ?? 0;

    const interstitialsData = Preferences.get(FTUEConstants.conversionsInterstitials);
    this._interstitials = interstitialsData ? JSON.parse(interstitialsData) : {};
  }

  private onCompleteStep(step: FTUEConversionsStep) {
    switch (step) {
      case 'playTrainingMatch1':
        FTUEController.setHasJoinedFirstFTUEMatch(true);

        // Mark these unneeded FTUE tutorials as completed
        FTUEController.setHasSeenVersusTutorials();

        // This fixes Android background -> foreground remounting the entire app
        // See https://github.com/skillz/sdk-mobile/pull/6219
        FTUEController.setLegacyFTUEComplete(true);
        break;
      case 'playTrainingMatch2':
        FTUEController.setHasSeenMatchSelectTutorial(true);

        // Mark these unneeded FTUE tutorials as completed
        FTUEController.setHasSeenPLOWCompletedTutorialTwo(true);
        FTUEController.setHasSeenPLOWCompletedTutorialOne(true);
        break;
    }
  }

  private addListeners() {
    // Reset the FTUE conversions flow when the user logs out
    GlobalEventEmitter.addListener('UserHasChanged', () => {
      this.reset();
    });
  }

  private reset() {
    Preferences.set({
      [FTUEConstants.conversionsStepIndex]: undefined,
      [FTUEConstants.conversionsInterstitials]: undefined,
      [FTUEConstants.conversionsDisabled]: undefined,
    });
    this.initializeFromPreferences();
  }

  private getTrainerAssetConfig(
    step: TutorialTrainerStep
  ):
    | Pick<FTUETrainerTextProps, 'trainerImage' | 'arrowSide' | 'bubbleAsset' | 'bubbleOffset'>
    | undefined {
    const bubbleAsset = this.textBubbleImage;
    switch (step) {
      case 'trainingMatch1Versus':
      case 'trainingMatch1Results':
      case 'trainingMatch2Versus':
      case 'trainingMatch2Results': {
        const uri = this.trainerAssets[TRAINER_ASSET_KEYS.faceLeft];
        if (!uri) return;
        return {
          // These dimensions are currently hard-coded; TODO: make these dynamic
          trainerImage: {source: {uri}, width: 200, height: 310},
          arrowSide: 'right',
          bubbleAsset,
          bubbleOffset: 144, // This also depends on the asset dimensions
        };
      }
      case 'trainingMatch2Select': {
        const uri = this.trainerAssets[TRAINER_ASSET_KEYS.faceRight];
        if (!uri) return;
        return {
          trainerImage: {source: {uri}, width: 200, height: 310},
          arrowSide: 'left',
          bubbleAsset,
          bubbleOffset: 144,
        };
      }
      default:
        return;
    }
  }

  private getTrainerText(
    step: TutorialTrainerStep,
    textValues: TutorialTrainerTextOptions['textValues'] = {}
  ): string | undefined {
    switch (step) {
      case 'trainingMatch1Versus':
        return I18n.message("Let's play a free training match to learn how the game works.");
      case 'trainingMatch1Results':
        return textValues.winningsAmount
          ? I18n.message(
              "Nice, you won {0}Z! You'll use Z to compete in matches once your training is complete.",
              textValues.winningsAmount
            )
          : I18n.message("Good job! Practice makes perfect, let's play another practice match.");
      case 'trainingMatch2Select':
        return I18n.message(
          "You're ready to up the stakes! This time we're playing for {0}Z.",
          textValues.matchRewardAmount ?? 10
        );
      case 'trainingMatch2Versus':
        return I18n.message(
          "After this you'll be ready to compete with other players, and even win cash & prizes!"
        );
      case 'trainingMatch2Results':
        return textValues.winningsAmount
          ? I18n.message("Congratulations! You're ready to take on the competition. Let's do this!")
          : I18n.message("Training complete! You're ready to take on the competition!");
      default:
        return;
    }
  }

  private getTrainerPosition(arrowSide: FTUETrainerTextProps['arrowSide'] = 'right') {
    // For smaller width screens, we need to offset the tutorial trainer since
    // we don't have enough room for both the trainer and text bubble
    //
    // This is done here rather than at the component level since we determine
    // other props for the trainer here as well, so it keeps things centralized
    const screenWidth = Dimensions.get('screen').width;
    const xOffset = screenWidth > 423 ? 0 : 423 - screenWidth;
    return {
      contentContainerStyle: arrowSide === 'left' ? {left: -xOffset} : {right: -xOffset},
    };
  }

  private get trainerAssets() {
    return ClientPropertyConstants.getTutorialTrainersImageSet();
  }

  private async loadTrainerImages() {
    const images = [this.textBubbleImage.uri];
    for (const key of Object.values(TRAINER_ASSET_KEYS)) {
      const uri = this.trainerAssets[key];
      if (!uri) continue;
      images.push(uri);
    }

    await Promise.all(images.map((uri) => Image.prefetch(uri)));
  }

  private getBubbleAnimationDelay(step: TutorialTrainerStep): number {
    switch (step) {
      case 'trainingMatch1Versus':
      case 'trainingMatch2Versus':
        // Includes additional delay for the slide in animation on the Versus
        // screen (see _animateAvatarEntrance in Versus.tsx)
        return 1000;
      case 'trainingMatch1Results':
      case 'trainingMatch2Results':
        // Includes additional delay for the fade in animation on the results
        // screen (see animateTutorialTrainer in RegularPlow.tsx)
        // Android doesn't prompt for notifications on the results screen, so we
        // don't need the delay there
        return Platform.select({ios: 700, default: 300});
      default:
        return 300;
    }
  }

  private get textBubbleImage() {
    return {uri: AssetHelper.pathForAssetNamed('image_text_bubble.png', false)};
  }
}

export default new FTUEConversionsController();
